/// <amd-module name="TFS/FeatureManagement/FeatureAdsService" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsRestClient } from "VSS/Platform/RestClient/Settings";

import FeatureResources = require("TFS/FeatureManagement/Resources");
import { FeatureCalloutComponent } from "./Components/FeatureBanner/FeatureCallout";

/**
 * Service for interacting with feature ads
 */
export interface IFeatureAdsService extends IVssService {
    /**
     * Dismiss the ad for the feature with the given id
     */
    dismissFeatureAd: (featureId: string) => Promise<void>;

    /**
     * Set a cookie to show the feature enabled callout
     */
    prepareShowFeatureEnabledCallout: (featureId: string) => void;

    /**
     * Show a callout that shows users where they can go to turn off a newly enabled feature
     */
    showFeatureEnabledCallout: (featureName: string, onDismiss?: () => void) => void;

    /**
     * Show a callout that shows users where they can go to manage features
     */
    showFeatureAdDismissedCallout: (onDismiss?: () => void) => void;
}

const featureBannerDismissedSettingPrefix = "FeatureAds/Dismissed/";
const featureBannerDismissedCalloutSetting = "FeatureAds/DismissCalloutShown";

class FeatureAdsService extends VssService implements IFeatureAdsService {
    public dismissFeatureAd(featureId: string): Promise<void> {
        const settingEntries: { [key: string]: any } = {};

        settingEntries[featureBannerDismissedSettingPrefix + featureId] = true;
        settingEntries[featureBannerDismissedCalloutSetting] = true;

        const settingsClient = this.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
        return settingsClient.setEntries(settingEntries, "me");
    }

    public prepareShowFeatureEnabledCallout(featureId: string) {
        // Set cookie for callout
        let secureFlag = "";
        if (window.location.protocol.toLowerCase().indexOf("https") === 0) {
            secureFlag = ";secure";
        }
        document.cookie = "Feature-Enabled-Callout=" + featureId + ";path=/;max-age=10" + secureFlag;
    }

    public showFeatureEnabledCallout(featureName: string, onDismiss?: () => void) {
        this.showFeatureCallout(
            format(FeatureResources.FeatureEnabledCalloutHeader, featureName),
            FeatureResources.FeatureEnabledCalloutBody,
            onDismiss
        );
    }

    public showFeatureAdDismissedCallout(onDismiss?: () => void) {
        this.showFeatureCallout(FeatureResources.FeatureDismissedCalloutHeader, FeatureResources.FeatureDismissedCalloutBody, onDismiss);
    }

    private showFeatureCallout(headerText: string, bodyFormat: string, onDismiss?: () => void) {
        let isHosted = false;

        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        if (pageData) {
            isHosted = pageData.isHosted;
        }

        const manageFeaturesMenuText = isHosted ? FeatureResources.PreviewFeaturesMenuHosted : FeatureResources.PreviewFeaturesMenuOnPrem;
        const bodyFormatArgs = [React.createElement("b", { key: "featureMenu" }, [manageFeaturesMenuText])];

        const container = document.createElement("div");
        document.body.appendChild(container);

        const onClose = () => {
            ReactDOM.unmountComponentAtNode(container);
            container.remove();

            if (onDismiss) {
                onDismiss();
            }
        };

        ReactDOM.render(
            React.createElement(FeatureCalloutComponent, {
                headerText,
                bodyFormat,
                bodyFormatArgs,
                onDismiss: onClose
            }),
            container
        );
    }
}

Services.add("IFeatureAdsService", { serviceFactory: FeatureAdsService });

class FeatureAdEnabledCalloutWrapper extends VssComponent<any, any> {
    public static componentType = "featureEnabledCallout";

    public constructor(props: any, context: IVssComponentContext) {
        super(props, context);

        const featureAdService = context.pageContext.getService<IFeatureAdsService>("IFeatureAdsService");
        featureAdService.showFeatureEnabledCallout(props.feature.name, props.onClose);
    }

    public render(): JSX.Element | null {
        return null;
    }
}

VssComponent.register(FeatureAdEnabledCalloutWrapper.componentType, FeatureAdEnabledCalloutWrapper);
